var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { TweenMax } from 'gsap';
import { ActivityManagerTyped as ActivityManager } from 'mixins/ActivityManager';
import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import HtmlRenderer from '../components/activity/HtmlRenderer.vue';
import MultiSelectCheckBoxes from '../components/activity/MultiSelectCheckBoxes.vue';
import TextEditor from '../components/inputs/TextEditor';
import { getHtmlText } from '../utils/html-util';
import ActivityWrapper from '../components/activity/ActivityWrapper.vue';
import KeyboardAdaptationWrapper from '../components/activity/KeyboardAdaptationWrapper.vue';
import mixins from 'vue-typed-mixins';
import { application, mapState } from '../store/helpers';
const TAB_1_HINT = 'TAB_1_HINT';
const TAB_1_INCORRECT_ATTEMPT = 'TAB_1_INCORRECT_ATTEMPT';
const TAB_2_EMPTY_ATTEMPT = 'TAB_2_EMPTY_ATTEMPT';
const TAB_2_INCORRECT_ATTEMPT = 'TAB_2_INCORRECT_ATTEMPT';
const TAB_2_INTRODUCTION = 'TAB_2_INTRODUCTION';
export default mixins(AssetLoader, TypingActivityHelper, ActivityManager(), ErrorLogger).extend({
    components: {
        HtmlRenderer,
        MultiSelectCheckBoxes,
        TextEditor,
        ActivityWrapper,
        KeyboardAdaptationWrapper
    },
    beforeDestroy() {
        this.hideKeyboard();
    },
    data() {
        return {
            activityName: 'MultiPart',
            activityStatus: {
                showErrors: false,
                showHints: false
            },
            currentTabIndex: 0,
            keyboardHtml: '',
            lockedCheckBoxSelections: [],
            multiSelectCheckBoxSelections: [],
            sfx: {},
            isComplete: false
        };
    },
    computed: Object.assign(Object.assign({}, mapState(application, ['device'])), { areMultiSelectAnswersCorrect() {
            return !!(this.multiSelectCheckBoxSelections.length &&
                this.correctSelectedMultiSelectAnswers.length ===
                    this.multiSelectCheckBoxSelections.length &&
                this.multiSelectCheckBoxSelections.length === this.maxMultiSelectAnswers);
        },
        correctSelectedMultiSelectAnswers() {
            return this.multiSelectCheckBoxSelections.filter(multiSelectCheckBoxSelectionId => !!this.currentRound.multiSelect.answers.find(answer => answer.correct && answer.id === multiSelectCheckBoxSelectionId));
        },
        isKeyboardAnswerCorrect() {
            return !!(!this.isOnFirstTab &&
                this.keyboardInput &&
                this.keyboardInput.length >= this.currentRound.constructAResponse.minCharacterCount);
        },
        isTablet() {
            return this.device.isMobile;
        },
        isOnFirstTab() {
            return !this.currentTabIndex;
        },
        keyboardInput() {
            return getHtmlText(this.keyboardHtml);
        },
        maxMultiSelectAnswers() {
            return this.currentRound && this.currentRound.multiSelect
                ? this.currentRound.multiSelect.answers.filter(answer => answer.correct).length
                : undefined;
        },
        multiSelectQuestionText() {
            return this.currentRound.multiSelect && this.currentRound.multiSelect.questionHtml;
        },
        constructAResponseQuestionText() {
            return (this.currentRound.constructAResponse && this.currentRound.constructAResponse.questionHtml);
        } }),
    methods: {
        getActivityState() {
            const { rounds } = this.activityData;
            return {
                instructions: {
                    delay: 1,
                    voice: rounds[0].multiSelect.instructions.audio.src
                },
                rounds
            };
        },
        loadActivity() {
            return __awaiter(this, void 0, void 0, function* () {
                const roundSetup = this.getActivityState();
                this.createRounds(roundSetup);
                const audiosMap = {
                    [TAB_1_HINT]: roundSetup.rounds[0].multiSelect.hint.audio.src,
                    [TAB_1_INCORRECT_ATTEMPT]: roundSetup.rounds[0].multiSelect.incorrectAttempt.audio.src,
                    [TAB_2_EMPTY_ATTEMPT]: roundSetup.rounds[0].constructAResponse.emptyAttempt.audio.src,
                    [TAB_2_INCORRECT_ATTEMPT]: roundSetup.rounds[0].constructAResponse.incorrectAttempt.audio.src,
                    [TAB_2_INTRODUCTION]: roundSetup.rounds[0].constructAResponse.instructions.audio.src
                };
                this.sfx = yield this.loadAudios(audiosMap);
            });
        },
        loadAudios(audiosMap) {
            return __awaiter(this, void 0, void 0, function* () {
                const soundPaths = Object.keys(audiosMap).map(key => audiosMap[key]);
                this.queueAudio(soundPaths);
                const cache = yield this.downloadAudio();
                const cachedAudiosMap = {};
                Object.keys(audiosMap).forEach(key => (cachedAudiosMap[key] = cache.get(audiosMap[key])));
                return cachedAudiosMap;
            });
        },
        handleCorrectKey() {
            this.sndType();
        },
        handleKeyboardTabOnError() {
            if (!this.keyboardInput) {
                this.playAudio(TAB_2_EMPTY_ATTEMPT);
                return;
            }
            this.playAudio(TAB_2_INCORRECT_ATTEMPT);
        },
        handleMultiSelectTabOnError() {
            this.activityStatus.showErrors = true;
            this.activityStatus.showHints = false;
            if (this.numberOfAttempts[0] === 1)
                this.playAudio(TAB_1_INCORRECT_ATTEMPT);
            TweenMax.delayedCall(1.5, () => {
                this.activityStatus.showErrors = false;
                if (this.numberOfAttempts[0] > 1) {
                    this.activityStatus.showHints = true;
                    this.playAudio(TAB_1_HINT);
                }
            });
        },
        handleMultiSelectTabSubmit() {
            this.incrementNumberOfAttempts();
            if (this.areMultiSelectAnswersCorrect) {
                TweenMax.delayedCall(1.5, () => {
                    this.currentTabIndex += 1;
                    this.hideKeyboard();
                    TweenMax.delayedCall(0.5, () => {
                        this.showKeyboard();
                        this.enableKeyboard();
                        this.setFreeTextTyping(true);
                        this.playAudio(TAB_2_INTRODUCTION);
                    });
                });
            }
            else {
                this.handleMultiSelectTabOnError();
            }
            this.lockedCheckBoxSelections = this.correctSelectedMultiSelectAnswers;
        },
        handleOnActivitySubmit() {
            if (this.currentTabIndex) {
                this.handleKeyboardTabSubmit();
                return;
            }
            this.handleMultiSelectTabSubmit();
        },
        handleOnCheckBoxesSelectionChange(checkBoxes) {
            this.multiSelectCheckBoxSelections = checkBoxes;
        },
        handleKeyboardTabSubmit() {
            if (this.isComplete) {
                return;
            }
            this.incrementNumberOfAttempts(1);
            if (this.isKeyboardAnswerCorrect) {
                this.isComplete = true;
                this.setResponseData([{}, { text: this.keyboardHtml }]);
                this.processEndOfActivity();
            }
            else {
                this.handleKeyboardTabOnError();
            }
        },
        playAudio(audioKey) {
            this.stopAllAudio();
            this.sfx[audioKey].play();
        },
        startActivity() {
            this.setupNewRound();
            this.errorLoggerStart();
            this.$nextTick(() => {
                TweenMax.to('.activity-stage', 0.5, { opacity: 1 });
            });
        },
        getKeyboardConfig() {
            const { keyboardType, roofs, showNumbers, characters } = this.activityData.keyboard;
            return {
                keyboardType,
                hideNumberRow: !showNumbers,
                characters,
                roofs
            };
        },
        stopAllAudio() {
            Object.keys(this.sfx).map(key => this.sfx[key].stop());
        }
    }
});
